import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useMode } from "@/lib/mode";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/cn";

/**
 * Polished handoff between Presentation Mode and Explore Mode.
 *
 * Plays after the final chapter. Three soft beats:
 *   1. The logo eases in alone, signaling the end.
 *   2. A quiet line of copy admits the story is done.
 *   3. A single call invites the investor to explore on their own terms.
 *
 * Then either auto-advances after a brief dwell or the user presses Enter /
 * clicks. The handoff exits Presentation Mode and restores the last Explore
 * path.
 */
export const EndingTransition = () => {
  const { exitPresent } = useMode();

  // Auto-advance dwell — long enough to feel intentional, short enough to
  // never feel slow. Investor can dismiss earlier with Enter or by clicking.
  useEffect(() => {
    const t = setTimeout(exitPresent, 9000);
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") {
        e.preventDefault();
        exitPresent();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [exitPresent]);

  return (
    <div
      data-testid="ending-transition"
      className="relative flex min-h-[60vh] w-full flex-col items-center justify-center text-center"
    >
      <div className="opacity-0 animate-fade-up [animation-delay:120ms]">
        <Logo size="lg" />
      </div>

      <p
        className={cn(
          "mt-12 max-w-xl text-balance text-2xl font-medium tracking-tight text-foreground sm:text-3xl",
          "opacity-0 animate-fade-up [animation-delay:900ms]"
        )}
        data-testid="ending-headline"
      >
        You&apos;ve seen the story.
      </p>
      <p
        className={cn(
          "mt-3 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg",
          "opacity-0 animate-fade-up [animation-delay:1700ms]"
        )}
      >
        Now explore whatever interests you.
      </p>

      <button
        type="button"
        onClick={exitPresent}
        data-testid="ending-enter-portal"
        className={cn(
          "mt-10 inline-flex items-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-colors hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "opacity-0 animate-fade-up [animation-delay:2400ms]"
        )}
      >
        Enter the portal
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default EndingTransition;
